import React, { useState } from "react";
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../../../convex/_generated/api";
import { Button } from "../../../../catalyst-components/button";
import { Badge } from "../../../../catalyst-components/badge";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "../../../../catalyst-components/table";
import { AdminRoutes, MainRoutes } from "../../../../shared/models";
import { Id } from "../../../../convex/_generated/dataModel";

const formatTimestamp = (timestamp: number) => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'breakthrough':
      return 'lime';
    case 'realization':
      return 'sky';
    case 'question':
      return 'amber';
    default:
      return 'zinc';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'high':
      return 'red';
    case 'medium':
      return 'amber';
    case 'low':
      return 'zinc';
    default:
      return 'zinc';
  }
};

export const SessionInsights: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'insights' | 'actions' | 'notes'>('insights');

  const returnPath = searchParams.get('from');
  const userId = searchParams.get('userId');

  const session = useQuery(
    api.sessions.getSessionById,
    sessionId && userId
      ? { session_id: sessionId as Id<"sessions">, user_id: userId as Id<"users">, is_personal_assistant: false }
      : "skip"
  );

  const insights = useQuery(
    api.session_insights.getBySession,
    sessionId ? { sessionId: sessionId as Id<"sessions"> } : "skip"
  );

  const actions = useQuery(
    api.session_action_items.getBySession,
    sessionId ? { sessionId: sessionId as Id<"sessions"> } : "skip"
  );

  const assistant = useQuery(
    api.assistants.getById,
    session?.assistant_id ? { id: session.assistant_id } : "skip"
  );

  const skillAssistant = useQuery(
    api.skill_assistants.getByAssistantId,
    session?.assistant_id ? { assistant_id: session.assistant_id } : "skip"
  );

  const skill = useQuery(
    api.skills.getById,
    skillAssistant?.skill_id ? { id: skillAssistant.skill_id } : "skip"
  );

  const user = useQuery(
    api.users.getUserById,
    userId ? { id: userId as Id<"users"> } : "skip"
  );

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-background pt-24 px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Session ID Required</h1>
          <Button color="light" onClick={() => navigate(AdminRoutes.Sessions)}>
            Back to Sessions
          </Button>
        </div>
      </div>
    );
  }

  if (!session || !insights || !actions) {
    return (
      <div className="min-h-screen bg-background pt-24 px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading session insights...</div>
        </div>
      </div>
    );
  }

  const handleBack = () => {
    if (returnPath === 'user' && userId) {
      navigate(`/admin/users/${userId}`);
    } else {
      navigate(AdminRoutes.Sessions);
    }
  };

  const tabs = [
    { id: 'insights', label: 'Insights', icon: '💡', count: insights.length },
    { id: 'actions', label: 'Action Items', icon: '✓', count: actions.length },
    { id: 'notes', label: 'Notes', icon: '📝', count: session.notes ? 1 : 0 }
  ];

  return (
    <div className="min-h-screen bg-background pt-24 px-6 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Session Insights</h1>
          <p className="text-gray-600 mt-1">
            {assistant?.display_name || assistant?.name || 'Unknown Assistant'}
            {skill && ` • ${skill.name}`}
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            color="light" 
            onClick={() => navigate(`${MainRoutes.Chat}?sessionId=${sessionId}&name=${user?.firstName} ${user?.lastName}&readonly=true`)}
          >
            View Chat
          </Button>
          <Button 
            color="light" 
            onClick={handleBack}
          >
            Back
          </Button>
        </div>
      </div>

      <div className="bg-white p-6 mb-6 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">User</h3>
            <p className="text-gray-900 mt-1">
              {user?.firstName && user?.lastName 
                ? `${user.firstName} ${user.lastName}`
                : user?.email || 'Unknown'
              }
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Status</h3>
            <Badge color={session.isComplete ? 'green' : 'amber'} className="mt-1">
              {session.isComplete ? 'Completed' : 'In Progress'}
            </Badge>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Insights</h3>
            <p className="text-gray-900 mt-1 text-2xl font-bold">{insights.length}</p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Actions</h3>
            <p className="text-gray-900 mt-1 text-2xl font-bold">{actions.length}</p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-primary-5 text-primary-6'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                {tab.label}
                <Badge color={activeTab === tab.id ? 'blue' : 'zinc'} className="ml-1">
                  {tab.count}
                </Badge>
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div>
        {activeTab === 'insights' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {insights.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No insights captured for this session.
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {insights.map((insight) => (
                  <div key={insight._id} className="p-6 hover:bg-gray-50">
                    <div className="flex items-start justify-between mb-2">
                      <Badge color={getCategoryColor(insight.category)}>
                        {insight.category}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        {formatTimestamp(insight.timestamp)}
                      </span>
                    </div>
                    <p className="text-gray-800 text-base leading-relaxed">
                      {insight.insight}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'actions' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {actions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No action items captured for this session.
              </div>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Action</TableHeader>
                    <TableHeader>Priority</TableHeader>
                    <TableHeader>Due Date</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Created</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {actions.map((action) => (
                    <TableRow key={action._id} className={action.completed ? 'opacity-60' : ''}>
                      <TableCell className="font-medium max-w-md">
                        <div className={`whitespace-normal break-words ${action.completed ? 'line-through text-gray-500' : ''}`}>
                          {action.action.replace(/<[^>]*>/g, '').replace(/[*_~`]/g, '')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge color={getPriorityColor(action.priority)}>
                          {action.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {action.dueDate ? (
                          <span className="text-sm">
                            {action.dueDate === 'this_week' && '📅 This Week'}
                            {action.dueDate === 'this_month' && '📅 This Month'}
                            {action.dueDate !== 'this_week' && action.dueDate !== 'this_month' && action.dueDate}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">No deadline</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge color={action.completed ? 'green' : 'amber'}>
                          {action.completed ? (
                            <>✓ Completed</>
                          ) : (
                            <>○ Pending</>
                          )}
                        </Badge>
                        {action.completed && action.completedAt && (
                          <div className="text-xs text-gray-500 mt-1">
                            {formatTimestamp(action.completedAt)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {formatTimestamp(action.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {session.notes ? (
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                  {session.notes}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                No notes saved for this session.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
